/**
 * 特殊牌型检测模块
 * 用于检测十三水游戏中的各种特殊牌型
 * 包括至尊青龙、一条龙、十二皇族、三同花顺、三套炸弹等
 */

import { 
  sortByRank, 
  getRankCounts, 
  getSuitCounts,
  SPECIAL_HANDS,
  Card,
  SUITS,
  RANKS,
  isJokerCard,
  buildSpecialHandDisplayLayout
} from './poker';
import { isStraightFlush, evaluateFiveCards, compareFiveCards, isValidLayout } from './handEvaluator';

/**
 * 为鬼牌生成所有可能的替代牌
 */
function getAllSubCards() {
  const cards = []
  const suits = Object.values(SUITS).filter(s => s.name !== 'joker')
  const ranks = Object.values(RANKS).filter(r => r.name !== 'Joker')
  for (const suit of suits) {
    for (const rank of ranks) {
      cards.push(new Card(suit, rank))
    }
  }
  return cards
}
const _allSubCards = getAllSubCards()

/**
 * 解析13张手牌中的鬼牌：枚举替代组合，优先凑出最高分的特殊牌型。
 * 返回 { resolvedCards, usedGhosts }
 */
function resolveGhostsIn13Cards(cards) {
  const ghosts = cards.filter(c => c.isJoker)
  if (ghosts.length === 0) return { resolvedCards: cards, usedGhosts: 0 }

  const naturalCards = cards.filter(c => !c.isJoker)
  const usedIds = new Set(naturalCards.map(c => c.id))
  const availableSubs = _allSubCards.filter(sc => !usedIds.has(sc.id))
  const ghostCount = ghosts.length

  let bestResolved = null
  let bestScore = -1

  function scoreResolved(resolved) {
    let maxScore = -1
    if (checkDragon(resolved)) maxScore = Math.max(maxScore, SPECIAL_HANDS.DRAGON.score)
    if (checkAllBig(resolved)) maxScore = Math.max(maxScore, SPECIAL_HANDS.ALL_BIG.score)
    if (checkAllSmall(resolved)) maxScore = Math.max(maxScore, SPECIAL_HANDS.ALL_SMALL.score)
    if (checkSixPairsHalf(resolved)) maxScore = Math.max(maxScore, SPECIAL_HANDS.SIX_PAIRS_HALF.score)
    return maxScore
  }

  function tryCombinations(depth, replacements, startIndex) {
    if (depth === ghostCount) {
      const resolved = [...naturalCards, ...replacements]
      const score = scoreResolved(resolved)
      if (bestResolved === null || score > bestScore) {
        bestResolved = resolved
        bestScore = score
      }
      return
    }

    for (let i = startIndex; i < availableSubs.length; i++) {
      replacements.push(availableSubs[i])
      tryCombinations(depth + 1, replacements, i + 1)
      replacements.pop()
    }
  }

  tryCombinations(0, [], 0)

  if (bestResolved) {
    return { resolvedCards: bestResolved, usedGhosts: ghostCount }
  }

  const fallback = [...naturalCards]
  for (let i = 0; i < ghostCount && i < availableSubs.length; i++) {
    fallback.push(availableSubs[i])
  }
  return { resolvedCards: fallback, usedGhosts: ghostCount }
}

// 检测至尊青龙（同花色的A-K十三张）
export function checkDragonUltimate(cards) {
  if (cards.length !== 13) return false;

  const suitCounts = getSuitCounts(cards);
  // 检查是否所有13张牌都是同一花色
  const hasOneSuit = Object.values(suitCounts).some(count => count === 13);

  if (!hasOneSuit) return false;

  // 检查是否包含A-K所有点数
  const ranks = new Set(cards.map(c => c.rank.value));
  return ranks.size === 13;
}

// 检测一条龙（A-K各一张，不同花色）
export function checkDragon(cards) {
  if (cards.length !== 13) return false;

  const rankCounts = getRankCounts(cards);

  // 检查是否每个点数恰好一张
  for (let i = 1; i <= 13; i++) {
    if (rankCounts[i] !== 1) return false;
  }

  return true;
}

// 检测十二皇族（全部12张人头牌J/Q/K/A）
export function checkRoyalFamily(cards) {
  if (cards.length !== 13) return false;

  // 筛选出J、Q、K、A这些人头牌（J=11, Q=12, K=13, A=1）
  const faceCards = cards.filter(c => Number(c.rank.value) >= 11 || Number(c.rank.value) === 1);
  return faceCards.length === 12;
}

// 检测三同花顺（三墩均为同花顺）
export function checkThreeStraightFlush(layout) {
  const { front, middle, back } = layout;

  // 验证三墩牌数量是否正确
  if (front.length !== 3 || middle.length !== 5 || back.length !== 5) {
    return false;
  }

  // 头道3张需要是同花顺（即同花且顺子）
  const frontFlush = checkIsFlush(front);
  const frontStraight = isThreeCardStraight(sortByRank(front, true));
  if (!frontFlush.isFlush || !frontStraight) return false;

  // 中道和尾道各5张，使用通用同花顺检测
  const middleSF = isStraightFlush(middle);
  const backSF = isStraightFlush(back);

  return middleSF.isStraightFlush && backSF.isStraightFlush;
}

// 检测三套炸弹（三墩均为铁支/葫芦，头道允许三条）
export function checkThreeBombs(layout) {
  const { front, middle, back } = layout;

  // 验证三墩牌数量是否正确
  if (front.length !== 3 || middle.length !== 5 || back.length !== 5) {
    return false;
  }

  // 头道必须是三条（3张牌点数相同）
  const frontRankCounts = getRankCounts(front);
  const frontHasThree = Object.values(frontRankCounts).some(c => c === 3);
  if (!frontHasThree) return false;

  // 中道和尾道必须是铁支或葫芦（rank.value >= 8 表示铁支或葫芦）
  const middleEval = evaluateFiveCards(middle);
  const backEval = evaluateFiveCards(back);

  const middleIsBomb = middleEval.rank.value >= 8; // 铁支或葫芦
  const backIsBomb = backEval.rank.value >= 8;

  return middleIsBomb && backIsBomb;
}

// 检测全大（全部为10-A）
export function checkAllBig(cards) {
  if (cards.length !== 13) return false;

  // 检查每张牌是否为10-A（A的值为1，其他10及以上为大牌）
  return cards.every(c => Number(c.rank.value) >= 10 || Number(c.rank.value) === 1);
}

// 检测全小（全部为2-9）
export function checkAllSmall(cards) {
  if (cards.length !== 13) return false;

  // 检查每张牌是否为2-9，不包含A
  return cards.every(c => Number(c.rank.value) >= 2 && Number(c.rank.value) <= 9);
}

// 检测凑一色（全部红桃/方块 或 全部黑桃/梅花）
export function checkOneColor(cards) {
  if (cards.length !== 13) return false;

  // 分别统计红色牌（红桃、方块）和黑色牌（黑桃、梅花）的数量
  const redCards = cards.filter(c => c.suit.color === 'red');
  const blackCards = cards.filter(c => c.suit.color === 'black');

  return redCards.length === 13 || blackCards.length === 13;
}

// 检测四套三条（四组三条 + 一张单牌）
export function checkFourThrees(cards) {
  if (cards.length !== 13) return false;

  const rankCounts = getRankCounts(cards);
  // 统计点数出现3次的牌组数量（三条）
  const threes = Object.values(rankCounts).filter(c => c === 3);
  // 统计点数出现1次的牌组数量（单牌）
  const singles = Object.values(rankCounts).filter(c => c === 1);

  return threes.length === 4 && singles.length === 1;
}

// 检测五对三条（五对 + 一个三条）
export function checkFivePairsThree(cards) {
  if (cards.length !== 13) return false;

  const rankCounts = getRankCounts(cards);
  // 统计点数出现2次的牌组数量（对子）
  const pairs = Object.values(rankCounts).filter(c => c === 2);
  // 统计点数出现3次的牌组数量（三条）
  const threes = Object.values(rankCounts).filter(c => c === 3);

  return pairs.length === 5 && threes.length === 1;
}

// 检测六对半（六对 + 一张单牌）
export function checkSixPairsHalf(cards) {
  if (cards.length !== 13) return false;

  const rankCounts = getRankCounts(cards);
  // 统计点数出现2次的牌组数量（对子）
  const pairs = Object.values(rankCounts).filter(c => c === 2);
  // 统计点数出现1次的牌组数量（单牌）
  const singles = Object.values(rankCounts).filter(c => c === 1);

  return pairs.length === 6 && singles.length === 1;
}

// 检测三同花（三墩均为同花）
export function checkThreeFlush(layout) {
  const { front, middle, back } = layout;

  // 验证三墩牌数量是否正确
  if (front.length !== 3 || middle.length !== 5 || back.length !== 5) {
    return false;
  }

  // 分别检测头道、中道、尾道是否为同花（鬼牌按百搭计入同花）
  const frontFlush = checkIsFlush(front);
  const middleFlush = checkIsFlush(middle);
  const backFlush = checkIsFlush(back);

  return frontFlush.isFlush && middleFlush.isFlush && backFlush.isFlush;
}

/**
 * 辅助函数：判断3张或5张牌是否是同花（鬼牌百搭）
 * 天然牌须同一花色，鬼牌可补足张数。
 * @param {Array} cards - 牌组（3张或5张）
 * @returns {Object} 返回 { isFlush: boolean, suit?: string }
 */
function checkIsFlush(cards) {
  if (cards.length !== 3 && cards.length !== 5) return { isFlush: false };

  const naturals = cards.filter(c => !isJokerCard(c));
  if (naturals.length === 0) {
    return { isFlush: true };
  }

  const suit = naturals[0].suit?.name || naturals[0].suit;
  const suitName = typeof suit === 'string' ? suit : '';
  if (!suitName || !naturals.every(c => (c.suit?.name || c.suit) === suitName)) {
    return { isFlush: false };
  }

  return { isFlush: true, suit: suitName };
}

function combinations(indices, k) {
  const result = [];
  const combo = [];
  const walk = (start) => {
    if (combo.length === k) {
      result.push(combo.slice());
      return;
    }
    for (let i = start; i < indices.length; i++) {
      combo.push(indices[i]);
      walk(i + 1);
      combo.pop();
    }
  };
  walk(0);
  return result;
}

/**
 * 从 13 张手牌中搜索可组成三同花的摆法（头3/中5/尾5，鬼牌百搭）。
 * 两道 5 张同花按点数逐张比大小（花色不计），弱的放中道、强的放尾道，保证头≤中≤尾。
 * @returns {{ front: Array, middle: Array, back: Array } | null}
 */
export function findThreeFlushLayout(cards) {
  if (!Array.isArray(cards) || cards.length !== 13) return null;

  const allIdx = cards.map((_, i) => i);
  const backCombos = combinations(allIdx, 5);

  for (const backIdx of backCombos) {
    const backSet = new Set(backIdx);
    const fiveA = backIdx.map(i => cards[i]);
    if (!checkIsFlush(fiveA).isFlush) continue;

    const remain1 = allIdx.filter(i => !backSet.has(i));
    const midCombos = combinations(remain1, 5);
    for (const midIdx of midCombos) {
      const fiveB = midIdx.map(i => cards[i]);
      if (!checkIsFlush(fiveB).isFlush) continue;

      const midSet = new Set(midIdx);
      const front = remain1.filter(i => !midSet.has(i)).map(i => cards[i]);
      if (!checkIsFlush(front).isFlush) continue;

      // 同花比牌只看点数，花色无大小：较弱 → 中道，较强 → 尾道
      let middle = fiveA;
      let back = fiveB;
      if (compareFiveCards(middle, back) > 0) {
        middle = fiveB;
        back = fiveA;
      }

      const layout = { front, middle, back };
      const valid = isValidLayout(front, middle, back);
      if (valid.valid) {
        return layout;
      }
    }
  }
  return null;
}

/**
 * 特殊牌型展示布局：三同花始终按合法强弱重排；其它优先用检测时附带摆法。
 */
export function resolveSpecialHandLayout(hands, specialHand) {
  if (specialHand?.type === 'THREE_FLUSH') {
    const found = findThreeFlushLayout(hands);
    if (found) return found;
  }
  if (specialHand?.layout?.front?.length === 3 &&
      specialHand?.layout?.middle?.length === 5 &&
      specialHand?.layout?.back?.length === 5) {
    return {
      front: [...specialHand.layout.front],
      middle: [...specialHand.layout.middle],
      back: [...specialHand.layout.back]
    };
  }
  return buildSpecialHandDisplayLayout(hands);
}

// 检测三顺子（三墩均为顺子，鬼牌可百搭补顺）
export function checkThreeStraight(layout) {
  const { front, middle, back } = layout;

  // 验证三墩牌数量是否正确
  if (front.length !== 3 || middle.length !== 5 || back.length !== 5) {
    return false;
  }

  // 头道 3 张、中道/尾道 5 张都按「能否凑成顺子」判断。
  // 鬼牌不按最大普通牌型（如配成对子），只要能补进顺子缺口即可，例如鬼+3+5 = 345。
  return canFormStraight(front, 3) && canFormStraight(middle, 5) && canFormStraight(back, 5);
}

function cardRankValue(card) {
  const rankRaw = card?.rank;
  if (typeof rankRaw === 'number') return Number(rankRaw);
  return Number(rankRaw?.value);
}

/** 长度为 3 或 5 的全部顺子窗口。A 低顺从 1 起，A 高顺用 14。 */
function straightWindows(length) {
  const windows = [];
  const wheel = [];
  for (let i = 0; i < length; i++) wheel.push(1 + i);
  windows.push(wheel);

  const maxStart = 14 - length + 1;
  for (let start = 2; start <= maxStart; start++) {
    const window = [];
    for (let i = 0; i < length; i++) window.push(start + i);
    windows.push(window);
  }
  return windows;
}

/**
 * 判断若干张牌能否组成指定长度的顺子。鬼牌百搭补缺张。
 * 天然牌点数必须互不相同。A 可作 1（A23 / A2345）或 14（QKA / 10JQKA）。
 */
function canFormStraight(cards, length) {
  if (!Array.isArray(cards) || cards.length !== length) return false;

  const ranks = [];
  let jokers = 0;
  for (const card of cards) {
    if (isJokerCard(card)) {
      jokers++;
      continue;
    }
    const value = cardRankValue(card);
    if (!value) return false;
    ranks.push(value);
  }

  if (jokers === length) return true;
  if (new Set(ranks).size !== ranks.length) return false;

  for (const window of straightWindows(length)) {
    const windowSet = new Set(window);
    const used = new Set();
    let ok = true;
    for (const rank of ranks) {
      const candidates = rank === 1 ? [1, 14] : [rank];
      const match = candidates.find(v => windowSet.has(v) && !used.has(v));
      if (match == null) {
        ok = false;
        break;
      }
      used.add(match);
    }
    if (ok && length - used.size <= jokers) return true;
  }
  return false;
}

/**
 * 辅助函数：判断3张牌是否是顺子（含鬼牌百搭）
 * 3张牌顺子的特殊情况：A-2-3 和 Q-K-A 也是顺子
 * @param {Array} cards - 3张牌
 * @returns {boolean} 是否为3张牌顺子
 */
function isThreeCardStraight(cards) {
  return canFormStraight(cards, 3);
}

// 检测所有特殊牌型，返回最高分的特殊牌型
export function detectSpecialHand(cards, layout = null) {
  // 如果有鬼牌，先解析为实际牌
  const ghostInfo = resolveGhostsIn13Cards(cards)
  const resolvedCards = ghostInfo.resolvedCards
  const usedGhosts = ghostInfo.usedGhosts

  const found = [];

  // 一条龙（A-K各一张）
  if (checkDragon(resolvedCards)) {
    found.push({
      type: 'DRAGON',
      ...SPECIAL_HANDS.DRAGON,
      usedGhosts
    });
  }

  // 全大（全部为10-A）
  if (checkAllBig(resolvedCards)) {
    found.push({
      type: 'ALL_BIG',
      ...SPECIAL_HANDS.ALL_BIG,
      usedGhosts
    });
  }

  // 全小（全部为2-9）
  if (checkAllSmall(resolvedCards)) {
    found.push({
      type: 'ALL_SMALL',
      ...SPECIAL_HANDS.ALL_SMALL,
      usedGhosts
    });
  }

  // 六对半（六对 + 一张单牌）
  if (checkSixPairsHalf(resolvedCards)) {
    found.push({
      type: 'SIX_PAIRS_HALF',
      ...SPECIAL_HANDS.SIX_PAIRS_HALF,
      usedGhosts
    });
  }

  // 三同花：可从 13 张手牌自动搜索（含鬼牌百搭），也可校验已有摆牌
  const threeFlushLayout = (layout && checkThreeFlush(layout))
    ? layout
    : findThreeFlushLayout(cards);
  if (threeFlushLayout) {
    found.push({
      type: 'THREE_FLUSH',
      ...SPECIAL_HANDS.THREE_FLUSH,
      usedGhosts,
      layout: {
        front: [...threeFlushLayout.front],
        middle: [...threeFlushLayout.middle],
        back: [...threeFlushLayout.back]
      }
    });
  }

  // 三顺子按当前摆牌判断（鬼牌可补顺，如头道鬼+3+5）
  if (layout && checkThreeStraight(layout)) {
    found.push({
      type: 'THREE_STRAIGHT',
      ...SPECIAL_HANDS.THREE_STRAIGHT,
      usedGhosts,
      layout: {
        front: [...layout.front],
        middle: [...layout.middle],
        back: [...layout.back]
      }
    });
  }

  // 未检测到任何特殊牌型
  if (found.length === 0) return null;

  found.sort((a, b) => b.score - a.score);
  return found[0];
}

/**
 * 比较两个特殊牌型的大小
 * 返回值：1表示hand1大，-1表示hand2大，0表示相等
 * 目前仅根据特殊牌型的基础分数进行比较
 * @param {Object} hand1 - 第一个特殊牌型
 * @param {Object} hand2 - 第二个特殊牌型
 * @returns {number} 比较结果
 */
export function compareSpecialHands(hand1, hand2) {
  const score1 = hand1.score;
  const score2 = hand2.score;

  // 分数不同时，直接按分数高低比较
  if (score1 !== score2) {
    return score1 > score2 ? 1 : -1;
  }

  // 分数相同时，根据具体牌型规则比较
  // 这里可以添加更详细的比较逻辑（如比较最大牌的点数等）
  return 0;
}
